import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ThingFuel,
  ThingTicket,
  ThingStamp,
  ActivityTennis,
  EventHistorical,
  ThingMovie,
  ActivityMusic,
  ActivityScience,
  ActivityFootball,
  ActivityBasketball,
  ActivityBaseball,
  ActivitySoccer,
  ThingFlag,
  DiscoveryPopulation,
  DiscoveryHeritage,
  DiscoveryNameMeaning,
  PlaceTemple,
  PlaceWorld,
} from '@fs/zion-icon'
import useEP from '../../../shared/hooks/useEP'
import { getTileData, formatTileContentData, getTileContentData } from '../services/services'
import { getCountryLanguageSelectOptions } from '../services/aamRegionLanguage'
import { newAncestorTemplate } from '../../../shared/helpers/person_Helper'
import { getDisplayName } from '../../../shared/helpers/name_formatting'

export const useTileData = () => {
  const [state, dispatch, func] = useEP(getTileData)

  return [state, func, dispatch]
}

export const useRegionLanguage = (t) => {
  const [regionLanguage, setRegionLanguage] = useState([])
  useEffect(() => {
    const regionLanguageOptions = getCountryLanguageSelectOptions(t)
    setRegionLanguage(regionLanguageOptions)
  }, [t])

  return regionLanguage
}

export const useTileContentData = ({ tileData, autoLoad = true }) => {
  const [state, dispatch, fn] = useEP(getTileContentData)

  useEffect(() => {
    if (autoLoad) {
      dispatch({
        type: 'load',
      })
    }
  }, [autoLoad, dispatch])

  useEffect(() => {
    if (state.load) {
      getTileContentData({ ...formatTileContentData(tileData) }).then((res) => {
        dispatch({ type: 'complete', res })
      })
    }
  }, [dispatch, state, tileData])

  return [state, dispatch, fn]
}

export const useAncestorYearMatch = (tileDataState, user, userPersonId, treeNameDataState, showRoman) => {
  const [t] = useTranslation()
  const [data, setData] = useState()

  useEffect(() => {
    if (!user?.userLoading && user?.birthDate && tileDataState?.complete && treeNameDataState?.complete) {
      const { birthDate } = user
      const { region, language } = tileDataState.res[0]

      const ancestorTiles = []
      treeNameDataState.res?.persons?.forEach((person) => {
        if (person.personId !== userPersonId) {
          const personYears = person?.lifespan.split('-')
          const personNameData =
            treeNameDataState?.complete &&
            treeNameDataState?.res?.persons?.find((item) => item?.personId === person?.personId)
          const isBirth = Number.parseInt(personYears[0], 10) === Number.parseInt(birthDate, 10)
          const isDeath = Number.parseInt(personYears[1], 10) === Number.parseInt(birthDate, 10)
          if (isBirth || isDeath) {
            ancestorTiles.push(
              newAncestorTemplate(
                birthDate,
                person,
                region,
                language,
                getDisplayName(personNameData, showRoman),
                isBirth ? t('aam.year-picker.ancestor-birth') : t('aam.year-picker.ancestor-death')
              )
            )
          }
        }
      })
      setData(ancestorTiles)
    }
  }, [user, showRoman, t, tileDataState, treeNameDataState, userPersonId])

  return data
}

export const useIconBgColor = (col = '') => {
  let iconBgColor = 'transparent'
  switch (col.toLowerCase()) {
    case '#bed21e':
      iconBgColor = '#318d43'
      break
    case '#6db344':
      iconBgColor = '#235c35'
      break
    case '#01b6d1':
      iconBgColor = '#01496e'
      break
    case '#7de3f4':
      iconBgColor = '#006184'
      break
    case '#ffb81c':
      iconBgColor = '#d45311'
      break
    case '#f68d2e':
      iconBgColor = '#a43500'
      break
    case '#fc4e6d':
      iconBgColor = '#8f124a'
      break
    default:
      iconBgColor = 'transparent'
  }

  return iconBgColor
}

export const useInnerTileVisuals = (imageUrl, color) => {
  const tileType = imageUrl ? imageUrl.split('/') : ''
  const iconBgColor = useIconBgColor(color)

  const cdnBaseUrl = 'https://common-discovery-service-pgp.familysearchcdn.org/aam/'
  let icon
  let background
  switch (tileType[tileType.length - 1]) {
    case 'icon_gas.svg':
      background = `Gas.jpg`
      icon = <ThingFuel color={color} background={iconBgColor} size="md" />
      break
    case 'icon_movie_ticket.svg':
      background = `MovieTicket.jpg`
      icon = <ThingTicket color={color} background={iconBgColor} size="md" />
      break
    case 'icon_stamp.svg':
      background = `Stamps.jpg`
      icon = <ThingStamp color={color} background={iconBgColor} size="md" />
      break
    case 'icon_tennis.svg':
      background = `Tennis.jpg`
      icon = <ActivityTennis color={color} background={iconBgColor} size="md" />
      break
    case 'icon_news.svg':
      background = `News.jpg`
      icon = <EventHistorical color={color} background={iconBgColor} size="md" />
      break
    case 'icon_movies.svg':
      background = `Movies.jpg`
      icon = <ThingMovie color={color} background={iconBgColor} size="md" />
      break
    case 'icon_music.svg':
      background = `Music.jpg`
      icon = <ActivityMusic color={color} background={iconBgColor} size="md" />
      break
    case 'icon_tech.svg':
      background = `Technology.jpg`
      icon = <ActivityScience color={color} background={iconBgColor} size="md" />
      break
    case 'icon_football.svg':
      background = `Football.jpg`
      icon = <ActivityFootball color={color} background={iconBgColor} size="md" />
      break
    case 'icon_basketball.svg':
      background = `Basketball.jpg`
      icon = <ActivityBasketball color={color} background={iconBgColor} size="md" />
      break
    case 'icon_baseball.svg':
      background = `Baseball.jpg`
      icon = <ActivityBaseball color={color} background={iconBgColor} size="md" />
      break
    case 'icon_soccer.svg':
      background = `Soccer.jpg`
      icon = <ActivitySoccer color={color} background={iconBgColor} size="md" />
      break
    case 'icon_president.svg':
      background = `President.jpg`
      icon = <ThingFlag color={color} background={iconBgColor} size="md" />
      break
    case 'icon_population-growth.svg':
      background = `Population.jpg`
      icon = <DiscoveryPopulation color={color} background={iconBgColor} size="md" />
      break
    case 'icon_temple_white.svg':
      background = `Temples.jpg`
      icon = <PlaceTemple color={color} background={iconBgColor} size="md" />
      break
    case 'icon_temple_dedication_white.svg':
      background = `Temples.jpg`
      icon = <PlaceTemple color={color} background={iconBgColor} size="md" />
      break
    case 'icon_prophet.svg':
      background = `Prophets.jpg`
      icon = <PlaceTemple color={color} background={iconBgColor} size="md" />
      break
    case 'icon_world.svg':
      background = `Population.jpg`
      icon = <PlaceWorld color={color} background={iconBgColor} size="md" />
      break
    case 'icon_heritage.svg':
      background = ``
      icon = <DiscoveryHeritage color={color} background={iconBgColor} size="md" />
      break
    default:
      background = ``
      icon = <DiscoveryNameMeaning color={color} background={iconBgColor} size="md" />
      break
  }

  const tileVisual = {
    icon,
    background: `${cdnBaseUrl}${background}`,
  }
  return tileVisual
}

export const useCorrections = () => {
  const splitLocation = window?.location.pathname.split('/')
  if (splitLocation?.includes('corrections') || splitLocation?.includes('correctionslogin')) {
    return true
  }
  return false
}

export const useComparisonByYear = (aamState, comparisonTargetPerson, dscState, tileData, comparisonYear) => {
  if (
    (aamState.isGuestUser &&
      comparisonTargetPerson &&
      comparisonTargetPerson.firstName !== dscState.nonstandardUser.firstName) ||
    (tileData?.personId && comparisonTargetPerson?.personId && tileData.personId !== comparisonTargetPerson.personId) ||
    (comparisonYear && tileData?.year && comparisonYear !== tileData?.year)
  ) {
    return true
  }
  return false
}

export const useComparisonByName = (
  aamState,
  comparisonTargetPerson,
  dscState,
  tileData,
  parsedNames,
  comparisonParsedNames
) => {
  if (
    (aamState?.isGuestUser &&
      comparisonTargetPerson &&
      comparisonTargetPerson.firstName !== dscState.nonstandardUser.firstName) ||
    (tileData?.personId && comparisonTargetPerson?.personId && tileData.personId !== comparisonTargetPerson.personId) ||
    (parsedNames?.firstName?.name &&
      comparisonParsedNames?.firstName?.name &&
      parsedNames?.firstName?.name !== comparisonParsedNames?.firstName?.name) ||
    (parsedNames?.lastName?.name &&
      comparisonParsedNames?.lastName?.name &&
      parsedNames?.lastName?.name !== comparisonParsedNames?.lastName?.name)
  ) {
    return true
  }
  return false
}
